import { useState, useCallback, ReactElement, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { TextField, Box, Typography, Alert, Button, CircularProgress } from '@mui/material';
import { useRoutes, AuthService, useApiEffect, useSnackbar, useKeyPress, formatErrorCode } from '@localstack/services';
import { LogoIcon } from '@localstack/ui';

import { AppRoute } from '~/config';
import { useAuthProvider } from '~/hooks/useAuthProvider';
import { BaseLayout } from '~/layouts';
import { KnownRedirects } from '~/views/customer/Redirect/constants';
import {
  MainContainer,
  ContentContainer,
  LogoContainer,
  InstructionsContainer,
  FooterContainer,
  EmailText,
} from '~/views/customer/Redirect/components';

const CODE_INPUTS = [0, 1, 2, 3] as const;

const useCodeInput = (length: number) => {
  const [code, setCode] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (index: number, value: string) => {
    const char = value?.toUpperCase().slice(-1) ?? '';
    setCode((prev) => {
      const next = [...prev];
      next[index] = char;
      return next;
    });
    if (char && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (index: number, text: string) => {
    const chars = text
      .toUpperCase()
      .split('')
      .slice(0, length - index);
    setCode((prev) => {
      const next = [...prev];
      chars.forEach((ch, i) => {
        next[index + i] = ch;
      });
      return next;
    });
    const nextIndex = Math.min(index + chars.length, length - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  const isComplete = code.every((c) => c.length === 1);
  const value = code.join('');

  return { code, setCode, inputRefs, handleChange, handlePaste, isComplete, value };
};

const checkIfProperCode = (code: string) => {
  return code.length === 4;
};

export const AuthRequestActivation = (): ReactElement => {
  const { goto } = useRoutes();
  const { userInfo } = useAuthProvider();
  const { requestId } = useParams<'requestId'>();
  const { showSnackbar } = useSnackbar();
  const [isCodeFieldDisabled, setIsCodeFieldDisabled] = useState<boolean>(false);

  const [searchParams] = useSearchParams();
  const codeInQueryParams = searchParams.get('code');

  const { code, setCode, inputRefs, handleChange, handlePaste, isComplete, value } = useCodeInput(CODE_INPUTS.length);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { confirmUserAuthRequest, error: apiError } = useApiEffect(
    AuthService,
    ['confirmUserAuthRequest', 'createUserAuthRequest'],
    {
      suppressErrors: true,
    },
  );

  useEffect(() => {
    if (!userInfo) goto(AppRoute.SIGN_IN);
  }, [userInfo]);

  useEffect(() => {
    if (!apiError) {
      return;
    }

    const error_code = formatErrorCode(apiError);
    if (error_code != 'auth.missing_initiator') {
      setError("We couldn't authorize this device. Check the code and try again.");
    } else {
      setError(
        'The client that initiated the request was too old. Please update to the latest version of LocalStack Desktop.',
      );
    }
  }, [apiError]);

  const confirm = useCallback(async () => {
    if (!isComplete || loading) return;
    if (!requestId) return;

    setLoading(true);
    setError('');

    const success = await confirmUserAuthRequest(requestId, { code: value });
    if (success) {
      goto(`${AppRoute.REDIRECT}?name=${KnownRedirects.AuthRequest}`);
      showSnackbar({ severity: 'success', message: 'Authenticated successfully' });
    }

    setLoading(false);
  }, [requestId, value]);

  useKeyPress(confirm, 'Enter');

  useEffect(() => {
    if (codeInQueryParams && checkIfProperCode(codeInQueryParams)) {
      setCode(codeInQueryParams.split(''));
      setIsCodeFieldDisabled(true);
    }
  }, [codeInQueryParams, setCode]);

  return (
    <BaseLayout title="Authorize Device" hideNavigation>
      <MainContainer>
        <ContentContainer>
          <LogoContainer>
            <LogoIcon />
          </LogoContainer>
          <InstructionsContainer>
            <Typography textAlign="center" variant="h2" component="h1" fontWeight={600}>
              Authorize Device
            </Typography>
            <Typography textAlign="center" variant="subtitle1" component="p" sx={{ mt: -0.5 }}>
              A device is requesting access to your account
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Enter authorization code
            </Typography>

            <Box display="flex" gap={1.5} justifyContent="center">
              {CODE_INPUTS.map((i) => (
                <TextField
                  key={`code-${i}`}
                  value={code[i]}
                  inputRef={(el) => (inputRefs.current[i] = el)}
                  autoFocus={i === 0}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !code[i]) {
                      inputRefs.current[i - 1]?.focus();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    handlePaste(i, e.clipboardData.getData('text'));
                  }}
                  inputProps={{ maxLength: 1, style: { textAlign: 'center', fontSize: '2rem' } }}
                  sx={{ width: 56, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  disabled={isCodeFieldDisabled}
                />
              ))}
            </Box>

            {error && (
              <Alert severity="error" sx={{ width: 260 }}>
                {error}
              </Alert>
            )}

            <Button
              color="primary"
              variant="contained"
              onClick={confirm}
              disabled={loading || !isComplete}
              sx={{ width: 260, height: 48 }}
            >
              Continue
              <CircularProgress size={14} sx={{ ml: 1, opacity: loading ? 1 : 0 }} />
            </Button>
          </InstructionsContainer>
          <FooterContainer>
            <EmailText>Logged in as {userInfo?.user?.email}</EmailText>
          </FooterContainer>
        </ContentContainer>
      </MainContainer>
    </BaseLayout>
  );
};
